import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { WheatBorder } from '../ui/WheatDecoration';
import { ErrorBoundary } from '../ErrorBoundary';
import { useSignup } from '../../context/SignupContext';
import { SignupStep, ValidationErrors } from '../../types';
import { validateSignupData, formatPhoneNumber, isValidPhoneNumber } from '../../utils/validation';
import { submitSignup } from '../../services/api';

export const SignupForm: React.FC = () => {
  const { setSignupData, setCurrentStep, isLoading, setIsLoading, setError } = useSignup();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  
  // Set focus on name input when component mounts
  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);
  
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: ''
  });
  
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isFormValid, setIsFormValid] = useState(false);
  
  // Validate form whenever form data changes
  useEffect(() => {
    const errors = validateSignupData(formData);
    setValidationErrors(errors);
    
    // Check if all required fields are filled and valid
    const isFullNameValid = formData.fullName.trim().length >= 2;
    const isPhoneValid = isValidPhoneNumber(formData.phoneNumber);
    
    setIsFormValid(isFullNameValid && isPhoneValid);
  }, [formData]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, fullName: value }));
  };
  
  // Track if we're currently handling a paste/autofill
  const isPasteOrAutofill = useRef(false);
  
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const value = input.value;
    const cursorPosition = input.selectionStart || 0;
    const previousValue = formData.phoneNumber;
    
    // Get the digit-only version of the previous and new values
    const previousDigits = previousValue.replace(/\D/g, '');
    const newDigits = value.replace(/\D/g, '');
    
    // Check if the user is trying to delete a character
    const isDeleting = previousValue.length > value.length || newDigits.length < previousDigits.length;
    
    // Format the new value
    let formatted = formatPhoneNumber(newDigits);
    
    // Update the state
    setFormData(prev => ({
      ...prev,
      phoneNumber: formatted
    }));
    
    // Maintain cursor position
    requestAnimationFrame(() => {
      if (input) {
        let newCursorPosition = cursorPosition;
        
        // If we're adding a character that caused formatting (like a parenthesis or dash)
        if (!isDeleting && formatted.length > value.length) {
          // Move cursor forward past the newly added formatting character
          newCursorPosition += (formatted.length - value.length);
        }
        
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    });
  };
  
  // Track autofill state
  const lastPhoneValue = useRef('');
  const autofillCheckTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Handle form submission when autofill is detected
  const handleSubmit = useCallback(async (e?: React.FormEvent, submitDataParam = formData) => {
    e?.preventDefault();

    // Mark all fields as touched
    setTouchedFields(new Set(['fullName', 'phoneNumber']));

    setIsLoading(true);
    setError('');

    try {
      const errors = validateSignupData(submitDataParam);
      setValidationErrors(errors);

      if (Object.keys(errors).length === 0) {
        await submitSignup(submitDataParam);
        setSignupData(submitDataParam);
        setCurrentStep(SignupStep.OTP_VERIFICATION);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during signup');
    } finally {
      setIsLoading(false);
    }
  }, [formData, setError, setIsLoading, setSignupData, setCurrentStep]);

  const handleAutofillSubmit = useCallback((phoneValue: string) => {
    if (!phoneValue) return false;

    const formatted = formatPhoneNumber(phoneValue);
    isPasteOrAutofill.current = true;

    // Update the form data
    const newFormData = {
      ...formData,
      phoneNumber: formatted
    };

    setFormData(newFormData);

    // If we have both name and phone, submit the form
    if (formData.fullName.trim() && isValidPhoneNumber(formatted)) {
      const submitErrors = validateSignupData(newFormData);
      if (Object.keys(submitErrors).length === 0) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          handleSubmit(undefined, newFormData);
        }, 50);
        return true;
      }
    }
    return false;
  }, [formData, handleSubmit]);
  // Check for autofilled values

  const checkForAutofill = useCallback(() => {
    if (!phoneInputRef.current) return false;

    const currentValue = phoneInputRef.current.value;
    const isFocused = document.activeElement === phoneInputRef.current;

    // If the value changed and the input isn't focused, it's likely autofill
    if (currentValue && currentValue !== lastPhoneValue.current && !isFocused) {
      lastPhoneValue.current = currentValue;
      return handleAutofillSubmit(currentValue);
    }

    return false;
  }, [handleAutofillSubmit]);
  // Set up autofill detection for phone number

  useEffect(() => {
    if (!phoneInputRef.current) return;

    // Store the ref in a variable to use in cleanup
    const phoneInput = phoneInputRef.current;

    // Check for autofill on various events
    const handleAutofillEvents = () => {
      if (autofillCheckTimeout.current) {
        clearTimeout(autofillCheckTimeout.current);
      }

      autofillCheckTimeout.current = setTimeout(() => {
        checkForAutofill();
      }, 100);
    };

    // Set up event listeners
    const events = ['input', 'change', 'blur', 'keyup'];

    // Initial check after a short delay
    const initialTimer = setTimeout(() => {
      checkForAutofill();
    }, 300);

    events.forEach(event => {
      phoneInput.addEventListener(event, handleAutofillEvents);
    });

    // Clean up
    return () => {
      clearTimeout(initialTimer);
      if (autofillCheckTimeout.current) {
        clearTimeout(autofillCheckTimeout.current);
      }
      events.forEach(event => {
        phoneInput.removeEventListener(event, handleAutofillEvents);
      });
    };
  }, [checkForAutofill]);

  const handleNameBlur = () => {
    setTouchedFields(prev => new Set(prev).add('fullName'));
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      phoneInputRef.current?.focus();
    }
  };

  const handleNamePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Get the pasted text
    const pastedText = e.clipboardData.getData('text/plain');
    // Update the form data with the pasted text
    setFormData(prev => ({
      ...prev,
      fullName: pastedText
    }));
    // Move focus to phone number field after a small delay
    // to allow the state to update
    setTimeout(() => {
      phoneInputRef.current?.focus();
    }, 10);
  };

  const handlePhonePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    
    const pastedText = e.clipboardData.getData('text/plain');
    const digitsOnly = pastedText.replace(/\D/g, '');
    
    // Only proceed if we have at least 10 digits
    if (digitsOnly.length < 10) {
      setValidationErrors(prev => ({
        ...prev,
        phoneNumber: 'Please enter a valid 10-digit US phone number'
      }));
      return;
    }
    
    const tenDigits = digitsOnly.slice(0, 10);
    const formattedPhone = formatPhoneNumber(tenDigits);
    
    // Mark that this is a paste operation
    isPasteOrAutofill.current = true;
    
    // Update the form state
    setFormData(prev => ({
      ...prev,
      phoneNumber: formattedPhone
    }));
    
    // Mark the field as touched
    setTouchedFields(prev => new Set(prev).add('phoneNumber'));
    
    // Clear any existing phone number errors
    setValidationErrors(prev => ({
      ...prev,
      phoneNumber: undefined
    }));
    
    // Auto-submit if we have both name and phone number
    if (formData.fullName.trim()) {
      const newFormData = {
        ...formData,
        phoneNumber: formattedPhone
      };
      
      const submitErrors = validateSignupData(newFormData);
      if (Object.keys(submitErrors).length === 0) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          handleSubmit(undefined, newFormData);
        }, 50);
      }
    }
  };
  
  const handlePhoneBlur = () => {
    setTouchedFields(prev => new Set(prev).add('phoneNumber'));
  };

  // Update validation errors when form data changes
  useEffect(() => {
    const errors = validateSignupData(formData);
    setValidationErrors(errors);
  }, [formData]);

  // Calculate validation errors to show
  const showErrors = (fieldName: keyof ValidationErrors): string | undefined => {
    return touchedFields.has(fieldName) ? validationErrors[fieldName] : undefined;
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="container">
          <Card className="relative overflow-hidden">
            <WheatBorder />
            <div className="relative z-10">
              <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-bakery-brown font-script mb-2">
                  Grace & Grain
                </h1>
                <p className="text-bakery-brown/80 text-lg">
                  Join our text ordering system for fresh baked goods
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    id="fullName"
                    label="Full Name"
                    value={formData.fullName}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    onKeyDown={handleNameKeyDown}
                    onPaste={handleNamePaste}
                    error={touchedFields.has('fullName') ? validationErrors.fullName : undefined}
                    placeholder="John Doe"
                    autoComplete="name"
                    inputMode="text"
                    enterKeyHint="next"
                    disabled={isLoading}
                    ref={nameInputRef}
                    required
                  />
                </div>

                <div>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    label="Phone Number"
                    placeholder="(555) 123-4567"
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    onPaste={handlePhonePaste}
                    onBlur={handlePhoneBlur}
                    onAnimationStart={(e: React.AnimationEvent<HTMLInputElement>) => {
                      // Some browsers trigger an animation when autofilling
                      if (e.animationName && e.animationName.includes('auto-fill')) {
                        setTimeout(checkForAutofill, 100);
                      }
                    }}
                    error={showErrors('phoneNumber')}
                    autoComplete="tel"
                    inputMode="tel"
                    enterKeyHint="done"
                    disabled={isLoading}
                    ref={phoneInputRef}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isFormValid || isLoading}
                  isLoading={isLoading}
                  onClick={(e) => {
                    // Ensure the form is submitted even if the button is disabled
                    if (!isFormValid) {
                      e.preventDefault();
                      // Mark all fields as touched to show validation errors
                      setTouchedFields(new Set(['fullName', 'phoneNumber']));
                    }
                  }}
                >
                  Sign Up for Text Ordering
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </ErrorBoundary>
  );
};
